/**
 * EditorToolbar.jsx
 * 
 * Top toolbar for the book editor with actions:
 * - Back button
 * - Undo/Redo
 * - Add page
 * - AI suggestions (future)
 * - Save status
 * - Continue button
 */

import React from 'react';
import { useEditor, useCanUndo, useCanRedo } from './EditorContext';
import {
  ArrowLeft,
  Undo2,
  Redo2,
  Plus,
  Sparkles,
  Layout,
  ChevronRight,
  Loader2,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

export default function EditorToolbar({
  onBack,
  onContinue,
  saveStatus,
  isDirty,
  pageCount
}) {
  const { undo, redo, addPage, toggleLayoutPicker } = useEditor();
  const canUndo = useCanUndo();
  const canRedo = useCanRedo();
  
  const handleAddPage = () => {
    addPage(null, {
      type: 'content',
      layout: 'hero',
      entries: [],
    });
  };
  
  return (
    <div className="bg-white border-b border-gray-200 px-4 py-3">
      <div className="flex items-center justify-between">
        {/* Left section - Back and undo/redo */}
        <div className="flex items-center gap-2">
          <button
            onClick={onBack}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Go back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          
          <div className="w-px h-6 bg-gray-200 mx-1" />
          
          <button
            onClick={undo}
            disabled={!canUndo}
            className={`p-2 rounded-lg transition-colors ${
              canUndo 
                ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100' 
                : 'text-gray-300 cursor-not-allowed'
            }`}
            aria-label="Undo"
            title="Undo (Cmd+Z)"
          >
            <Undo2 className="w-5 h-5" />
          </button>
          
          <button
            onClick={redo}
            disabled={!canRedo}
            className={`p-2 rounded-lg transition-colors ${
              canRedo 
                ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100' 
                : 'text-gray-300 cursor-not-allowed'
            }`}
            aria-label="Redo"
            title="Redo (Cmd+Shift+Z)"
          >
            <Redo2 className="w-5 h-5" />
          </button>
        </div>
        
        {/* Center section - Page count and add */}
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-600 hidden sm:inline">
            {pageCount} pages
          </span>
          
          <button
            onClick={handleAddPage}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-ocean-700 bg-ocean-50 hover:bg-ocean-100 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Add Page</span>
          </button>
          
          {/* Layout picker toggle (mobile only) */}
          <button
            onClick={() => toggleLayoutPicker(true)}
            className="lg:hidden flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <Layout className="w-4 h-4" />
            <span className="hidden sm:inline">Layout</span>
          </button>
          
          {/* Future: AI suggestions button */}
          {/* <button
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
          >
            <Sparkles className="w-4 h-4" />
            <span className="hidden sm:inline">AI Suggest</span>
          </button> */}
        </div>
        
        {/* Right section - Save status and continue */}
        <div className="flex items-center gap-3">
          {/* Save status indicator */}
          <div className="hidden sm:flex items-center gap-1.5 text-sm">
            {saveStatus === 'saving' && (
              <>
                <Loader2 className="w-4 h-4 animate-spin text-ocean-600" />
                <span className="text-gray-500">Saving...</span>
              </>
            )}
            {saveStatus === 'ready' && !isDirty && (
              <>
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span className="text-gray-500">Saved</span>
              </>
            )}
            {saveStatus === 'error' && (
              <>
                <AlertCircle className="w-4 h-4 text-red-500" />
                <span className="text-red-600">Save failed</span>
              </>
            )}
          </div>
          
          <button
            onClick={onContinue}
            disabled={saveStatus === 'saving'}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              saveStatus === 'saving'
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-ocean-600 text-white hover:bg-ocean-700'
            }`}
          >
            <span>Continue</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
